import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  employeeIncidentService,
  INCIDENT_APPROVAL_OUTCOME,
  INCIDENT_REJECTION_OUTCOME,
} from '@/api/domains/employeeIncident';
import { useUI } from '@/hooks/ui/useUI';


interface UseIncidentResolutionParams {
  incidentId: number;
  onApproved?: () => void;
  onRejected?: () => void;
  onConflict?: () => void;
}

export interface UseIncidentResolutionResult {
  comment: string;
  setComment: (value: string) => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  handleApprove: () => Promise<void>;
  handleReject: () => Promise<void>;
}

/**
 * Lógica de resolución (aprobación/rechazo) individual de una incidencia de
 * marcaje (ASIS-5247 / REXASIS-005 y ASIS-5248 / REXASIS-006). Ambas acciones
 * comparten el mismo comentario del aprobador — un único campo de texto en el
 * panel de detalle — por lo que un solo hook administra el estado compartido
 * (`comment`, `isSubmitting`) y expone un handler dedicado por acción.
 */
export function useIncidentResolution({
  incidentId,
  onApproved,
  onRejected,
  onConflict,
}: UseIncidentResolutionParams): UseIncidentResolutionResult {
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, showWarning } = useUI();

  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = comment.trim().length > 0 && !isSubmitting;

  const handleApprove = useCallback(async () => {
    if (comment.trim().length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await employeeIncidentService.approve(incidentId, {
        approverComment: comment.trim(),
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:incidentManagement.messages.approveError'));
        return;
      }

      const data = response.data;

      if (data.outcome === INCIDENT_APPROVAL_OUTCOME.ALREADY_RESOLVED) {
        showWarning(data.message || translate('attendance:incidentManagement.messages.alreadyResolved'));
        onConflict?.();
        return;
      }

      showSuccess(data.message || translate('attendance:incidentManagement.messages.approveSuccess'));
      onApproved?.();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    comment,
    isSubmitting,
    incidentId,
    showApiError,
    showWarning,
    showSuccess,
    translate,
    onApproved,
    onConflict,
  ]);

  const handleReject = useCallback(async () => {
    if (comment.trim().length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await employeeIncidentService.reject(incidentId, {
        approverComment: comment.trim(),
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:incidentManagement.messages.rejectError'));
        return;
      }

      const data = response.data;

      if (data.outcome === INCIDENT_REJECTION_OUTCOME.ALREADY_RESOLVED) {
        showWarning(data.message || translate('attendance:incidentManagement.messages.alreadyResolved'));
        onConflict?.();
        return;
      }

      showSuccess(data.message || translate('attendance:incidentManagement.messages.rejectSuccess'));
      onRejected?.();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    comment,
    isSubmitting,
    incidentId,
    showApiError,
    showWarning,
    showSuccess,
    translate,
    onRejected,
    onConflict,
  ]);

  return {
    comment,
    setComment,
    canSubmit,
    isSubmitting,
    handleApprove,
    handleReject,
  };
}

export default useIncidentResolution;
